import { PlusOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Modal, Upload } from "antd";
import { useState } from "react";
import { RiDeleteBin7Line } from "react-icons/ri";
import apiFactory from "../../../api";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";

import {
  getBase64,
  openNotificationError,
  openNotificationSuccess,
  resetSearch,
} from "../../../utils/Utils";
import { useImageLoader } from "../../customHook/ImageLoader";
import "./EditGroupModal.scss";
import { uploadMultipartFile } from "../../../api/authentication/storageApi";
import { compressImage } from "../../../utils/SendFile";

const EditGroupModal = ({ data, isOpen, onClose }) => {
  const { user, languageMap } = useInfoUser();
  const {
    getConversation,
    setSelectedConversation,
    handleUpdateConversationList,
    conversationList,
    selectedConversation,
    isSearching,
    setIsSearching,
    setSearchInputValue,
    setSearchConversationList,
    setSearchName,
  } = useMenuContext();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState({
    base64: data?.avatar,
  });
  const [initialValues, setInitialValues] = useState({
    groupName: data?.name,
    groupAvatar: data?.avatar,
  });

  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(data?.avatar);

  const close = () => {
    onClose();
    form.resetFields();
    setUploadingAvatar({
      base64: data?.avatar,
    });
  };

  const onFinish = async (values) => {
    try {
      let mainPath = null;
      let thumbnailPreviewPath = null;

      if (uploadingAvatar?.originFileObj) {
        const originalFile = uploadingAvatar.originFileObj;
        const MAX_AVATAR_SIZE = 500 * 1024;
        const MAX_DIMENSION = 500;

        let compressedFile = null;

        if (
          originalFile.size > MAX_AVATAR_SIZE ||
          originalFile.width > MAX_DIMENSION ||
          originalFile.height > MAX_DIMENSION
        ) {
          compressedFile = await compressImage(originalFile);
        }

        if (compressedFile) {
          const [originalUploadRes, compressedUploadRes] = await Promise.all([
            uploadMultipartFile({ file: originalFile, bucket: "avatar" }),
            uploadMultipartFile({ file: compressedFile, bucket: "avatar" }),
          ]);

          mainPath = originalUploadRes?.location;
          thumbnailPreviewPath = compressedUploadRes?.location;
        } else {
          const uploadRes = await uploadMultipartFile({
            file: originalFile,
            bucket: "avatar",
          });

          mainPath = uploadRes?.location;
        }
      }

      const request = {
        conversationId: data?.conversationId,
        conversationName: null,
        type: data?.type,
        mainPath,
        thumbnailPreviewPath,
      };

      if (values?.groupName?.trim() !== data?.name) {
        request.conversationName = values?.groupName?.trim();
      }

      const result =
        await apiFactory.userConversationApi.updateConversation(request);

      if (result?.status === 200) {
        setSelectedConversation({
          ...selectedConversation,
          name: values?.groupName,
          avatar: thumbnailPreviewPath || mainPath,
        });

        const updatedConversationList = conversationList?.map((item) => {
          if (item?.conversationId === data?.conversationId) {
            return { ...item, name: values?.groupName };
          }
          return item;
        });

        handleUpdateConversationList(updatedConversationList);

        openNotificationSuccess(result?.message);

        if (isSearching) {
          resetSearch(
            setIsSearching,
            setSearchInputValue,
            setSearchConversationList,
            setSearchName,
          );
        }

        onClose();
      } else {
        openNotificationError(result?.message);
      }
    } catch (error) {
      console.error("Lỗi cập nhật thông tin nhóm:", error);
      openNotificationError("Có lỗi xảy ra trong quá trình xử lý!");
    } finally {
      // setIsLoading(false);
    }
  };

  const isImage = (fileName) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".jfif",
      ".png",
      ".apng",
      ".gif",
      ".bmp",
      ".dib",
      ".svg",
      ".webp",
      ".avif",
      ".jxl",
      ".heic",
      ".heif",
      ".ico",
      ".tif",
      ".tiff",
    ];
    const fileExtension = fileName
      .toLowerCase()
      .substring(fileName.lastIndexOf("."));
    return imageExtensions.includes(fileExtension);
  };

  const upload = async (uploadFiles) => {
    const file = uploadFiles?.fileList[0];

    if (file && file.status !== "uploading") {
      if (isImage(file.name)) {
        setUploadingAvatar({
          base64: await getBase64(file.originFileObj),
          fileName: file.name,
          originFileObj: file.originFileObj,
        });
        setInitialValues({ ...initialValues, groupAvatar: null });
      } else {
        openNotificationError(
          languageMap?.["error.onlyUpImg"] ?? "You can only upload image!",
        );
      }
    }
  };

  const CustomImage = ({ src, alt, onRemove }) => {
    return (
      <div className="custom-image-container">
        <Image
          src={src}
          alt={alt}
          width={80}
          height={80}
          preview={false}
          className="image"
          style={{
            objectFit: "cover",
          }}
        />

        <RiDeleteBin7Line
          size={17}
          className="custom-remove-icon"
          onClick={onRemove}
        />
      </div>
    );
  };

  const removeAvatar = (e) => {
    setUploadingAvatar(null);
    initialValues.groupAvatar = [];
    e?.stopPropagation();
  };

  const showAvatar = () => {
    if (uploadingAvatar?.base64) {
      // if uploadingAvatar is old url then length < 100
      // else if uploadingAvatar is new base64 then length > 100
      if (uploadingAvatar?.base64?.length < 100) {
        return <CustomImage src={imageUrl} onRemove={removeAvatar} />;
      } else {
        return (
          <CustomImage src={uploadingAvatar?.base64} onRemove={removeAvatar} />
        );
      }
    } else {
      return (
        <button
          style={{
            border: 0,
            background: "none",
          }}
          type="button"
        >
          <PlusOutlined />
          <div
            style={{
              marginTop: 8,
            }}
          >
            {languageMap?.["modal.conversation.upload"] ?? "Upload"}
          </div>
        </button>
      );
    }
  };

  return (
    <Modal
      title={
        data?.type !== "PERSONAL"
          ? languageMap?.["modal.conversation.editGroup"] ?? "Edit group"
          : languageMap?.["modal.conversation.editPersonal"] ?? "Edit personal"
      }
      open={isOpen}
      onCancel={close}
      footer={false}
      closeIcon={false}
      closable
    >
      <Form
        onFinish={onFinish}
        autoComplete="off"
        layout="horizontal"
        initialValues={initialValues}
        form={form}
        labelCol={{
          span: 6,
        }}
        // onKeyDown={preventSubmitOnEnter}
      >
        <Form.Item
          className="mt-8"
          name="groupName"
          label={
            data?.type !== "PERSONAL"
              ? languageMap?.["modal.conversation.groupName"] ?? "Group name"
              : languageMap?.["modal.conversation.personalName"] ??
                "Personal name"
          }
          rules={[
            {
              required: true,
              message: "Required!",
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === "") {
                  return Promise.reject(
                    new Error("Group name cannot be empty or just spaces!"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input type="text" disabled={isLoading} maxLength={70} />
        </Form.Item>
        {data?.type !== "PERSONAL" && (
          <Form.Item
            // name="groupAvatar"
            label={
              languageMap?.["modal.conversation.groupImage"] ?? "Group image"
            }
            valuePropName="fileList"
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              showUploadList={false}
              onChange={upload}
            >
              {showAvatar()}
            </Upload>
          </Form.Item>
        )}
        <div className="flex gap-[10px] justify-end">
          <Button className="font-medium" type="primary" htmlType="submit">
            {languageMap?.["project.management.config.updateBtn"] ?? "Update"}
          </Button>
          <Button
            className="!bg-[#ffffff] !text-black hover:!bg-[#d9d9d9] font-medium"
            onClick={close}
          >
            {languageMap?.["project.management.config.CancelBtn"] ?? "Cancel"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditGroupModal;
